import Phaser from "phaser";

import { GAME_AUDIO } from "../config/audioAssets";
import { FACET_QUARTER_ART, PREMIUM_SHOP_ART } from "../config/artAssets";
import { GAME_CONFIG } from "../config/gameConfig";
import type { DiamondSkinId } from "../domain/types";

export type WorkshopTap = {
  x: number;
  y: number;
};

export type WorkshopDiamondLayout = {
  left: number;
  top: number;
  width: number;
  height: number;
  canvasWidth: number;
  canvasHeight: number;
  upgraded: boolean;
};

export type WorkshopDiamondMotionSnapshot = {
  selectedSkinId: DiamondSkinId | null;
  interactionBlocked: boolean;
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
  tapFeedbackCount: number;
  musicEnabled: boolean;
  musicLoadState: "idle" | "loading" | "ready" | "error";
  musicPlaying: boolean;
  musicPaused: boolean;
  musicLocked: boolean;
  musicGestureSeen: boolean;
  musicContextState: string;
  musicVolume: number;
};

export type WorkshopSceneCallbacks = {
  onTap(point: WorkshopTap): void;
  onEnergyDepleted?(): void;
  onMusicUnavailable?(): void;
  onReady?(): void;
};

const HERO_WIDTH = 292;
const HERO_HIT_WIDTH = 320;
const HERO_HIT_HEIGHT = 276;
type WorkshopMusic = Phaser.Sound.NoAudioSound | Phaser.Sound.HTML5AudioSound | Phaser.Sound.WebAudioSound;

export class KineticWorkshopScene extends Phaser.Scene {
  readonly #callbacks: WorkshopSceneCallbacks;
  #tapRewardLabel: string;
  readonly #renderScale: number;
  #energyCurrent: number;
  #energyCapacity: number;
  #workshopStage: 1 | 2 | 3 | 4 = 1;
  #selectedSkinId: DiamondSkinId | null;
  #musicEnabled: boolean;
  #musicGestureSeen = false;
  #musicSuspended = false;
  #musicLoadState: "idle" | "loading" | "ready" | "error" = "idle";
  #musicUnavailableReported = false;
  #music: WorkshopMusic | null = null;
  #reducedMotion = false;
  #networkOnline = true;
  #economyActive = true;
  #paused = false;
  #interactionBlocked = false;
  #onboardingFocus = false;
  #pendingUpgrade = false;
  #diamondBaseY = 0;
  #diamondScale = 1;
  #diamondBaseImageScale = 1;
  #diamondUpgradedImageScale = 1;
  #diamondSkinScales = new Map<DiamondSkinId, number>();
  #layoutWidth = 0;
  #layoutHeight = 0;
  #tapFeedbackCount = 0;

  #background!: Phaser.GameObjects.Image;
  #diamondShadow!: Phaser.GameObjects.Ellipse;
  #diamond!: Phaser.GameObjects.Container;
  #diamondBase!: Phaser.GameObjects.Image;
  #diamondUpgraded!: Phaser.GameObjects.Image;
  #diamondSkins = new Map<DiamondSkinId, Phaser.GameObjects.Image>();
  #diamondGlow!: Phaser.GameObjects.Arc;
  #hitArea!: Phaser.GameObjects.Zone;
  #networkVeil!: Phaser.GameObjects.Rectangle;
  #sparks: Phaser.GameObjects.Arc[] = [];
  #rewards: Phaser.GameObjects.Text[] = [];
  #sparkCursor = 0;
  #rewardCursor = 0;
  #ambientTweens: Phaser.Tweens.Tween[] = [];

  constructor(
    callbacks: WorkshopSceneCallbacks,
    tapReward: string,
    energyCurrent: number,
    energyCapacity: number,
    selectedSkinId: DiamondSkinId | null,
    musicEnabled: boolean,
    renderScale: number,
  ) {
    super({ key: "KineticWorkshop" });
    this.#callbacks = callbacks;
    this.#tapRewardLabel = `+${tapReward}`;
    this.#energyCurrent = energyCurrent;
    this.#energyCapacity = Math.max(1, energyCapacity);
    this.#selectedSkinId = selectedSkinId;
    this.#musicEnabled = musicEnabled;
    this.#renderScale = renderScale;
  }

  preload(): void {
    this.load.image(FACET_QUARTER_ART.homeBackground.key, FACET_QUARTER_ART.homeBackground.url);
    this.load.image(FACET_QUARTER_ART.diamond.base.key, FACET_QUARTER_ART.diamond.base.url);
    this.load.image(FACET_QUARTER_ART.diamond.upgraded.key, FACET_QUARTER_ART.diamond.upgraded.url);
    if (this.#selectedSkinId !== null) {
      const asset = PREMIUM_SHOP_ART.skins[this.#selectedSkinId];
      this.load.image(asset.key, asset.url);
    }
  }

  create(): void {
    this.#background = this.add.image(0, 0, FACET_QUARTER_ART.homeBackground.key).setDepth(0);
    this.#diamondShadow = this.add
      .ellipse(0, 0, 222, 48, 0x0b5360, 0.28)
      .setDepth(1)
      .setBlendMode(Phaser.BlendModes.MULTIPLY);
    this.#diamondGlow = this.add
      .circle(0, 18, 58, 0x75edff, 0.22)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.#diamondBase = this.add.image(0, 0, FACET_QUARTER_ART.diamond.base.key);
    this.#diamondUpgraded = this.add
      .image(0, 0, FACET_QUARTER_ART.diamond.upgraded.key)
      .setAlpha(0)
      .setVisible(false);
    if (this.#selectedSkinId !== null) this.#createSkinImage(this.#selectedSkinId);
    this.#sizeDiamondImages();
    this.#diamond = this.add
      .container(0, 0, [this.#diamondGlow, this.#diamondBase, this.#diamondUpgraded, ...this.#diamondSkins.values()])
      .setDepth(4);

    this.#createPools();

    this.#hitArea = this.add
      .zone(0, 0, HERO_HIT_WIDTH, HERO_HIT_HEIGHT)
      .setDepth(6)
      .setInteractive({ useHandCursor: true });
    this.#hitArea.on(Phaser.Input.Events.POINTER_DOWN, (pointer: Phaser.Input.Pointer) => {
      if (this.#paused || this.#interactionBlocked) return;
      const point = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
      if (!this.#networkOnline || !this.#economyActive) {
        this.#callbacks.onTap({ x: point.x, y: point.y });
        return;
      }
      if (!this.#consumeEnergy()) {
        this.#reactToDepletedEnergy();
        this.#callbacks.onEnergyDepleted?.();
        return;
      }
      this.#callbacks.onTap({ x: point.x, y: point.y });
      // App refreshes the effective reward at this tap's server-anchored time.
      this.#reactToTap(point.x, point.y);
    });

    this.#networkVeil = this.add
      .rectangle(0, 0, 10, 10, 0x123342, 0.48)
      .setOrigin(0)
      .setDepth(8)
      .setVisible(false);

    this.scale.on(Phaser.Scale.Events.RESIZE, this.#layout, this);
    this.sound.on(Phaser.Sound.Events.UNLOCKED, this.#syncMusic, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.sound.off(Phaser.Sound.Events.UNLOCKED, this.#syncMusic, this);
      this.#music?.destroy();
      this.#music = null;
    });
    this.#layout({ width: this.scale.width, height: this.scale.height } as Phaser.Structs.Size);
    this.#applyStage(false);
    this.#callbacks.onReady?.();
    // Warm and decode audio immediately after the first playable frame. This
    // keeps the visual launch fast while ensuring a later iOS toggle never has
    // to begin loading or decoding the track after its user gesture.
    this.time.delayedCall(0, () => this.#ensureMusicLoaded());
  }

  setWorkshopStage(stage: 1 | 2 | 3 | 4): void {
    if (this.#workshopStage === stage) return;
    this.#workshopStage = stage;
    if (this.sys.isActive() && !this.#paused) this.#applyStage(false);
  }

  setSelectedSkin(selectedSkinId: DiamondSkinId | null): void {
    if (this.#selectedSkinId === selectedSkinId) return;
    this.#selectedSkinId = selectedSkinId;
    if (!this.sys.isActive()) return;
    if (selectedSkinId === null || this.#diamondSkins.has(selectedSkinId)) {
      this.#applyStage(true);
      return;
    }
    this.#loadSkin(selectedSkinId);
  }

  setReducedMotion(reduced: boolean): void {
    if (this.#reducedMotion === reduced) return;
    this.#reducedMotion = reduced;
    if (this.sys.isActive()) this.#restartAmbientMotion();
  }

  setMusicEnabled(enabled: boolean): void {
    if (this.#musicEnabled === enabled) {
      if (enabled) this.#syncMusic();
      return;
    }
    this.#musicEnabled = enabled;
    if (!enabled) {
      this.#stopMusic();
      return;
    }
    this.#musicUnavailableReported = false;
    this.#ensureMusicLoaded();
  }

  setMusicSuspended(suspended: boolean): void {
    if (this.#musicSuspended === suspended) return;
    this.#musicSuspended = suspended;
    if (suspended) {
      if (this.#music?.isPlaying === true) this.#music.pause();
      return;
    }
    this.#syncMusic();
  }

  handleUserGesture(): void {
    this.#musicGestureSeen = true;
    if (this.sound.locked) this.sound.unlock();
    if (this.sound instanceof Phaser.Sound.WebAudioSoundManager && this.sound.context.state !== "running") {
      void this.sound.context.resume().then(
        () => {
          this.#syncMusic();
          this.time.delayedCall(180, () => this.#syncMusic());
        },
        () => undefined,
      );
      return;
    }
    this.#syncMusic();
  }

  setNetworkOnline(online: boolean): void {
    this.#networkOnline = online;
    if (this.sys.isActive()) this.#networkVeil.setVisible(!online);
  }

  setEconomyActive(active: boolean): void {
    this.#economyActive = active;
  }

  setOnboardingFocus(active: boolean): void {
    if (this.#onboardingFocus === active) return;
    this.#onboardingFocus = active;
    if (!this.sys.isActive()) return;
    this.#ambientTweens.forEach((tween) => tween.stop());
    this.#ambientTweens = [];
    this.tweens.killTweensOf(this.#diamond);
    this.#diamond.setPosition(this.#layoutWidth / 2, this.#diamondBaseY).setScale(this.#diamondScale);
    if (!active) this.#restartAmbientMotion();
  }

  playTapFeedback(x?: number, y?: number): boolean {
    if (
      this.#paused ||
      this.#interactionBlocked ||
      !this.sys.isActive() ||
      !this.#networkOnline ||
      !this.#economyActive
    ) return false;
    if (!this.#consumeEnergy()) {
      this.#reactToDepletedEnergy();
      this.#callbacks.onEnergyDepleted?.();
      return false;
    }
    this.#reactToTap(x ?? this.#diamond.x, y ?? this.#diamond.y);
    return true;
  }

  setEnergyState(current: number, capacity: number): void {
    this.#energyCapacity = Math.max(1, capacity);
    this.#energyCurrent = Math.max(0, Math.min(current, this.#energyCapacity));
  }

  #tapBoostActive = false;

  setTapReward(tapReward: string, boosted = false): void {
    this.#tapRewardLabel = `+${tapReward}`;
    this.#tapBoostActive = boosted;
  }

  getDiamondLayout(): WorkshopDiamondLayout {
    const skinId = this.#selectedSkinId;
    const upgraded = skinId !== null || this.#workshopStage >= 2;
    const asset = skinId !== null
      ? PREMIUM_SHOP_ART.skins[skinId]
      : upgraded
        ? FACET_QUARTER_ART.diamond.upgraded
        : FACET_QUARTER_ART.diamond.base;
    const imageScale = skinId !== null
      ? this.#diamondSkinScales.get(skinId) ?? 1
      : upgraded
        ? this.#diamondUpgradedImageScale
        : this.#diamondBaseImageScale;
    const width = asset.width * imageScale * this.#diamondScale;
    const height = asset.height * imageScale * this.#diamondScale;
    return {
      left: this.#layoutWidth / 2 - width / 2,
      top: this.#diamondBaseY - height / 2,
      width,
      height,
      canvasWidth: this.#layoutWidth,
      canvasHeight: this.#layoutHeight,
      upgraded,
    };
  }

  getDiamondMotionSnapshot(): WorkshopDiamondMotionSnapshot {
    return {
      selectedSkinId: this.#selectedSkinId,
      interactionBlocked: this.#interactionBlocked,
      x: this.#diamond.x,
      y: this.#diamond.y,
      scaleX: this.#diamond.scaleX,
      scaleY: this.#diamond.scaleY,
      tapFeedbackCount: this.#tapFeedbackCount,
      musicEnabled: this.#musicEnabled,
      musicLoadState: this.#musicLoadState,
      musicPlaying: this.#music?.isPlaying ?? false,
      musicPaused: this.#music?.isPaused ?? false,
      musicLocked: this.sound?.locked ?? true,
      musicGestureSeen: this.#musicGestureSeen,
      musicContextState: this.sound instanceof Phaser.Sound.WebAudioSoundManager
        ? this.sound.context.state
        : "unavailable",
      musicVolume: this.#music?.volume ?? 0,
    };
  }

  #ensureMusicLoaded(): void {
    if (!this.sys.isActive()) return;
    if (this.cache.audio.exists(GAME_AUDIO.workshopTheme.key)) {
      this.#musicLoadState = "ready";
      this.#syncMusic();
      return;
    }
    if (this.#musicLoadState === "loading") return;
    this.#musicLoadState = "loading";
    const onError = (file: Phaser.Loader.File) => {
      if (file.key !== GAME_AUDIO.workshopTheme.key) return;
      this.load.off(Phaser.Loader.Events.FILE_LOAD_ERROR, onError);
      this.#musicLoadState = "error";
      if (this.#musicEnabled) this.#reportMusicUnavailable();
    };
    this.load.on(Phaser.Loader.Events.FILE_LOAD_ERROR, onError);
    this.load.once(Phaser.Loader.Events.COMPLETE, () => {
      this.load.off(Phaser.Loader.Events.FILE_LOAD_ERROR, onError);
      if (!this.cache.audio.exists(GAME_AUDIO.workshopTheme.key)) {
        this.#musicLoadState = "error";
        if (this.#musicEnabled) this.#reportMusicUnavailable();
        return;
      }
      this.#musicLoadState = "ready";
      this.#syncMusic();
    });
    this.load.audio(GAME_AUDIO.workshopTheme.key, GAME_AUDIO.workshopTheme.url);
    this.load.start();
  }

  #syncMusic(): void {
    if (!this.sys.isActive() || !this.#musicEnabled || this.#musicSuspended || !this.#musicGestureSeen) return;
    if (!this.cache.audio.exists(GAME_AUDIO.workshopTheme.key)) {
      this.#ensureMusicLoaded();
      return;
    }
    if (this.sound.locked) return;
    if (this.#music === null) {
      this.#music = this.sound.add(GAME_AUDIO.workshopTheme.key, {
        loop: true,
        volume: 0,
      });
      // Keep the first unlocked frame silent as well. Some browser audio
      // backends expose the default gain until the sound is explicitly set.
      this.#music.setVolume(0);
    }
    if (this.#music.isPaused) this.#music.resume();
    else if (!this.#music.isPlaying) this.#music.play({ loop: true, volume: 0 });
    if (!this.#music.isPlaying) return;
    if (this.#music.volume > GAME_AUDIO.workshopTheme.volume + 0.005) this.#music.setVolume(0);
    this.tweens.killTweensOf(this.#music);
    this.tweens.add({
      targets: this.#music,
      volume: GAME_AUDIO.workshopTheme.volume,
      duration: 900,
      ease: "Sine.easeOut",
    });
  }

  #stopMusic(): void {
    const music = this.#music;
    if (music === null) return;
    this.tweens.killTweensOf(music);
    if (!music.isPlaying) {
      music.stop();
      music.setVolume(0);
      return;
    }
    this.tweens.add({
      targets: music,
      volume: 0,
      duration: 260,
      ease: "Sine.easeIn",
      onComplete: () => {
        if (this.#musicEnabled || this.#music !== music) return;
        music.stop();
        music.setVolume(0);
      },
    });
  }

  #reportMusicUnavailable(): void {
    if (this.#musicUnavailableReported) return;
    this.#musicUnavailableReported = true;
    this.#callbacks.onMusicUnavailable?.();
  }

  playUpgrade(): void {
    if (this.#paused || !this.sys.isActive()) {
      this.#pendingUpgrade = true;
      return;
    }
    this.#applyStage(true);
  }

  setVisualPaused(paused: boolean): void {
    if (this.#paused === paused) return;
    this.#paused = paused;
    if (paused) {
      this.#ambientTweens.forEach((tween) => tween.stop());
      this.#ambientTweens = [];
    } else {
      const animateUpgrade = this.#pendingUpgrade;
      this.#pendingUpgrade = false;
      this.#applyStage(animateUpgrade);
    }
    this.#syncHitAreaInput();
  }

  setInteractionBlocked(blocked: boolean): void {
    if (this.#interactionBlocked === blocked) return;
    this.#interactionBlocked = blocked;
    this.#syncHitAreaInput();
  }

  #syncHitAreaInput(): void {
    if (!this.sys.isActive() || this.#hitArea === undefined || this.#hitArea.input === null) return;
    this.#hitArea.input.enabled = !this.#paused && !this.#interactionBlocked;
  }

  #sizeDiamondImages(): void {
    const base = FACET_QUARTER_ART.diamond.base;
    const upgraded = FACET_QUARTER_ART.diamond.upgraded;
    this.#diamondBaseImageScale = HERO_WIDTH / base.width;
    this.#diamondUpgradedImageScale = HERO_WIDTH / upgraded.width;
    this.#diamondBase.setScale(this.#diamondBaseImageScale);
    this.#diamondUpgraded.setScale(this.#diamondUpgradedImageScale);
    this.#diamondSkins.forEach((image, skinId) => {
      image.setScale(this.#skinScale(skinId));
    });
  }

  #skinScale(skinId: DiamondSkinId): number {
    const existing = this.#diamondSkinScales.get(skinId);
    if (existing !== undefined) return existing;
    const scale = HERO_WIDTH / PREMIUM_SHOP_ART.skins[skinId].width;
    this.#diamondSkinScales.set(skinId, scale);
    return scale;
  }

  #createSkinImage(skinId: DiamondSkinId): Phaser.GameObjects.Image {
    const existing = this.#diamondSkins.get(skinId);
    if (existing !== undefined) return existing;
    const image = this.add
      .image(0, 0, PREMIUM_SHOP_ART.skins[skinId].key)
      .setScale(this.#skinScale(skinId))
      .setAlpha(0)
      .setVisible(false);
    this.#diamondSkins.set(skinId, image);
    if (this.#diamond !== undefined) this.#diamond.add(image);
    return image;
  }

  #loadSkin(skinId: DiamondSkinId): void {
    const asset = PREMIUM_SHOP_ART.skins[skinId];
    if (this.textures.exists(asset.key)) {
      this.#createSkinImage(skinId);
      if (this.#selectedSkinId === skinId) this.#applyStage(true);
      return;
    }
    this.load.once(Phaser.Loader.Events.COMPLETE, () => {
      this.#createSkinImage(skinId);
      if (this.#selectedSkinId === skinId) this.#applyStage(true);
    });
    this.load.image(asset.key, asset.url);
    this.load.start();
  }

  #createPools(): void {
    for (let index = 0; index < 24; index += 1) {
      const color = index % 3 === 0 ? 0xeab56d : index % 2 === 0 ? 0xd7fcff : 0x33bbc3;
      this.#sparks.push(this.add.circle(0, 0, index % 4 === 0 ? 3 : 2, color, 1).setDepth(7).setVisible(false));
    }

    for (let index = 0; index < 6; index += 1) {
      this.#rewards.push(
        this.add
          .text(0, 0, this.#tapRewardLabel, {
            color: "#fff7d8",
            fontFamily: "Arial, sans-serif",
            fontSize: "24px",
            fontStyle: "bold",
            stroke: "#145061",
            strokeThickness: 5,
          })
          .setOrigin(0.5)
          .setDepth(7)
          .setVisible(false),
      );
    }
  }

  #layout(size: Phaser.Structs.Size): void {
    const physicalWidth = Math.round(size.width);
    const physicalHeight = Math.round(size.height);
    const width = Math.round(physicalWidth / this.#renderScale);
    const height = Math.round(physicalHeight / this.#renderScale);
    if (width <= 0 || height <= 0 || (width === this.#layoutWidth && height === this.#layoutHeight)) return;
    this.#layoutWidth = width;
    this.#layoutHeight = height;
    this.cameras.main
      .setViewport(0, 0, physicalWidth, physicalHeight)
      .setZoom(this.#renderScale)
      .centerOn(width / 2, height / 2);
    const backgroundScale = Math.max(
      width / FACET_QUARTER_ART.homeBackground.width,
      height / FACET_QUARTER_ART.homeBackground.height,
    );
    this.#background
      .setPosition(width / 2, height / 2)
      .setScale(backgroundScale);

    this.#diamondScale = Phaser.Math.Clamp(Math.min(width / 350, height / 410), 0.82, 1.2);
    this.#diamondBaseY = Math.round(height * (height <= 640 ? 0.52 : 0.49));
    this.#diamond.setPosition(width / 2, this.#diamondBaseY).setScale(this.#diamondScale);
    this.#hitArea
      .setPosition(width / 2, this.#diamondBaseY)
      .setSize(HERO_HIT_WIDTH * this.#diamondScale, HERO_HIT_HEIGHT * this.#diamondScale);
    this.#diamondShadow
      .setPosition(width / 2, this.#diamondBaseY + 126 * this.#diamondScale)
      .setScale(this.#diamondScale);
    this.#networkVeil.setSize(width, height).setVisible(!this.#networkOnline);
    this.#restartAmbientMotion();
  }

  #consumeEnergy(): boolean {
    if (this.#energyCurrent < GAME_CONFIG.energy.costPerTap) return false;
    this.#energyCurrent -= GAME_CONFIG.energy.costPerTap;
    return true;
  }

  #reactToDepletedEnergy(): void {
    if (this.#paused || !this.sys.isActive()) return;
    this.tweens.killTweensOf(this.#diamondGlow);
    this.#diamondGlow.setFillStyle(0xe5a159, 0.5).setAlpha(0.5).setScale(1.03);
    this.tweens.add({
      targets: this.#diamondGlow,
      alpha: 0.12,
      scale: 0.96,
      duration: this.#reducedMotion ? 100 : 260,
      ease: "Quad.easeOut",
      onComplete: () => this.#applyStage(false),
    });
  }

  #reactToTap(x: number, y: number): void {
    this.#tapFeedbackCount += 1;
    this.#ambientTweens.forEach((tween) => tween.stop());
    this.#ambientTweens = [];
    this.tweens.killTweensOf(this.#diamond);
    this.tweens.killTweensOf(this.#diamondGlow);

    const pressScaleX = this.#diamondScale * (this.#reducedMotion ? 0.985 : 0.965);
    const pressScaleY = this.#diamondScale * (this.#reducedMotion ? 1.015 : 1.035);
    this.#diamond
      .setPosition(this.#layoutWidth / 2, this.#diamondBaseY + (this.#reducedMotion ? 1 : 3))
      .setScale(pressScaleX, pressScaleY);
    this.tweens.add({
      targets: this.#diamond,
      y: this.#diamondBaseY - (this.#reducedMotion ? 1 : 5),
      scaleX: this.#diamondScale * (this.#reducedMotion ? 1.012 : 1.045),
      scaleY: this.#diamondScale * (this.#reducedMotion ? 0.992 : 0.97),
      duration: this.#reducedMotion ? 45 : 82,
      ease: "Quad.easeOut",
      onComplete: () => {
        this.tweens.add({
          targets: this.#diamond,
          y: this.#diamondBaseY,
          scaleX: this.#diamondScale,
          scaleY: this.#diamondScale,
          duration: this.#reducedMotion ? 55 : 128,
          ease: this.#reducedMotion ? "Sine.easeOut" : "Back.easeOut",
          onComplete: () => this.#restartAmbientMotion(),
        });
      },
    });
    this.#diamondGlow.setAlpha(this.#workshopStage >= 2 ? 0.82 : 0.58).setScale(1.12);
    this.tweens.add({
      targets: this.#diamondGlow,
      alpha: this.#workshopStage >= 2 ? 0.34 : 0.2,
      scale: 1,
      duration: this.#reducedMotion ? 100 : 240,
      ease: "Quad.easeOut",
    });

    this.#showReward(x, y);
    this.#emitSparks(this.#diamond.x, this.#diamond.y - 8);
  }

  #showReward(x: number, y: number): void {
    const reward = this.#rewards[this.#rewardCursor];
    this.#rewardCursor = (this.#rewardCursor + 1) % this.#rewards.length;
    if (reward === undefined) return;

    this.tweens.killTweensOf(reward);
    reward.setText(this.#tapRewardLabel).setColor(this.#tapBoostActive ? "#ffda78" : "#fff7d8")
      .setPosition(x, y - 8).setAlpha(1).setScale(1).setVisible(true);
    this.tweens.add({
      targets: reward,
      y: this.#reducedMotion ? y - 16 : y - 62,
      alpha: 0,
      scale: this.#reducedMotion ? 0.98 : 1.1,
      duration: this.#reducedMotion ? 240 : GAME_CONFIG.motion.tapRewardMs,
      ease: "Cubic.easeOut",
      onComplete: () => reward.setVisible(false),
    });
  }

  #emitSparks(x: number, y: number): void {
    const count = this.#reducedMotion ? 3 : 8;
    for (let index = 0; index < count; index += 1) {
      const spark = this.#sparks[this.#sparkCursor];
      this.#sparkCursor = (this.#sparkCursor + 1) % this.#sparks.length;
      if (spark === undefined) continue;

      this.tweens.killTweensOf(spark);
      const angle = Phaser.Math.DegToRad(-160 + index * (320 / Math.max(1, count - 1)));
      const distance = this.#reducedMotion ? 20 : 42 + (index % 3) * 12;
      spark.setPosition(x, y).setAlpha(1).setScale(1).setVisible(true);
      this.tweens.add({
        targets: spark,
        x: x + Math.cos(angle) * distance,
        y: y + Math.sin(angle) * distance,
        alpha: 0,
        scale: 0.35,
        duration: this.#reducedMotion ? 150 : 430,
        ease: "Quad.easeOut",
        onComplete: () => spark.setVisible(false),
      });
    }
  }

  #applyStage(animate: boolean): void {
    const upgraded = this.#workshopStage >= 2;
    const selectedSkinId = this.#selectedSkinId;
    const selectedSkin = selectedSkinId === null ? null : this.#diamondSkins.get(selectedSkinId) ?? null;
    this.tweens.killTweensOf(this.#diamondBase);
    this.tweens.killTweensOf(this.#diamondUpgraded);
    this.#diamondSkins.forEach((image) => this.tweens.killTweensOf(image));
    this.tweens.killTweensOf(this.#diamondGlow);

    if (selectedSkin !== null && selectedSkinId !== null) {
      this.#diamondBase.setVisible(false).setAlpha(0);
      this.#diamondUpgraded.setVisible(false).setAlpha(0);
      this.#diamondSkins.forEach((image) => image.setVisible(false).setAlpha(0));
      const skinScale = this.#diamondSkinScales.get(selectedSkinId) ?? 1;
      selectedSkin
        .setVisible(true)
        .setAlpha(animate && !this.#reducedMotion ? 0 : 1)
        .setScale(skinScale * (animate && !this.#reducedMotion ? 0.9 : 1));
      const glowColor = selectedSkinId === "skin-solar-heart"
        ? 0xffc66c
        : selectedSkinId === "skin-black-diamond"
          ? 0x3dd7da
          : 0x75f6e7;
      this.#diamondGlow.setFillStyle(glowColor, 0.44).setAlpha(0.44).setScale(1.04);
      if (animate && !this.#reducedMotion) {
        this.tweens.add({
          targets: selectedSkin,
          alpha: 1,
          scaleX: skinScale,
          scaleY: skinScale,
          duration: GAME_CONFIG.motion.upgradeMs,
          ease: "Back.easeOut",
        });
        this.#emitSparks(this.#diamond.x, this.#diamond.y);
      }
      this.#diamondShadow.setFillStyle(0x075865, 0.36);
      this.#restartAmbientMotion();
      return;
    }

    this.#diamondSkins.forEach((image, skinId) => {
      image.setVisible(false).setAlpha(0).setScale(this.#diamondSkinScales.get(skinId) ?? 1);
    });

    if (animate && upgraded && !this.#reducedMotion) {
      this.#diamondBase.setVisible(true).setAlpha(1).setScale(this.#diamondBaseImageScale);
      this.#diamondUpgraded
        .setVisible(true)
        .setAlpha(0)
        .setScale(this.#diamondUpgradedImageScale * 0.88);
      this.#diamondGlow.setFillStyle(0x66f5ff, 0.86).setAlpha(0.86).setScale(0.65);
      this.tweens.add({
        targets: this.#diamondBase,
        alpha: 0,
        scale: this.#diamondBaseImageScale * 1.08,
        duration: GAME_CONFIG.motion.upgradeMs,
        ease: "Cubic.easeIn",
        onComplete: () => this.#diamondBase.setVisible(false),
      });
      this.tweens.add({
        targets: this.#diamondUpgraded,
        alpha: 1,
        scale: this.#diamondUpgradedImageScale,
        duration: GAME_CONFIG.motion.upgradeMs,
        ease: "Back.easeOut",
      });
      this.tweens.add({
        targets: this.#diamondGlow,
        alpha: 0.34,
        scale: 1,
        duration: GAME_CONFIG.motion.upgradeMs,
        ease: "Cubic.easeOut",
      });
      this.#emitSparks(this.#diamond.x, this.#diamond.y);
    } else {
      this.#diamondBase
        .setVisible(!upgraded)
        .setAlpha(upgraded ? 0 : 1)
        .setScale(this.#diamondBaseImageScale);
      this.#diamondUpgraded
        .setVisible(upgraded)
        .setAlpha(upgraded ? 1 : 0)
        .setScale(this.#diamondUpgradedImageScale);
      this.#diamondGlow
        .setFillStyle(upgraded ? 0x65efff : 0xbdf7ff, upgraded ? 0.34 : 0.2)
        .setAlpha(upgraded ? 0.34 : 0.2)
        .setScale(1);
    }

    this.#diamondShadow.setFillStyle(upgraded ? 0x075865 : 0x235f66, upgraded ? 0.34 : 0.26);
    this.#restartAmbientMotion();
  }

  #restartAmbientMotion(): void {
    this.#ambientTweens.forEach((tween) => tween.stop());
    this.#ambientTweens = [];
    this.tweens.killTweensOf(this.#diamond);
    this.#diamond.setY(this.#diamondBaseY).setScale(this.#diamondScale);
    if (this.#reducedMotion || this.#paused || this.#onboardingFocus) return;

    this.#ambientTweens.push(
      this.tweens.add({
        targets: this.#diamond,
        y: this.#diamondBaseY - 6,
        duration: 2_200,
        yoyo: true,
        repeat: -1,
        ease: "Sine.easeInOut",
      }),
    );
    if (this.#workshopStage >= 2) {
      this.#diamondGlow.setAlpha(0.3).setScale(0.98);
      this.#ambientTweens.push(
        this.tweens.add({
          targets: this.#diamondGlow,
          scale: 1.04,
          duration: 2_600,
          yoyo: true,
          repeat: -1,
          ease: "Sine.easeInOut",
        }),
      );
    }
  }
}
